<template>
  <v-container fluid class="persona-profile pa-6" v-if="persona">
    <!-- Header Section with Navigation and Title -->
    <v-row class="header-section mb-8">
      <v-col cols="12">
        <v-btn
          color="primary"
          variant="tonal"
          @click="goBack"
          prepend-icon="mdi-arrow-left"
          rounded="pill"
          density="comfortable"
          class="elevation-1"
        >
          {{ t('personainfo.backToPersonas') }}
        </v-btn>
      </v-col>
      <v-col cols="12" class="text-center">
        <h1 class="text-h3 font-weight-bold gradient-text">{{ personaDisplayName }}</h1>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <v-row>
      <!-- Profile Image Card -->
      <v-col cols="12" md="3">
        <v-card class="profile-card elevation-3 rounded-xl overflow-hidden h-100">
          <div class="profile-header primary pa-4"></div>
          <div class="profile-image-container text-center">
            <v-avatar size="160" class="profile-avatar mt-n10 elevation-4">
              <v-img
                :src="!generated ? getPersonaImage(persona) : (staticAssetPath(generatedDataMap['image']) || 'https://randomuser.me/api/portraits/lego/5.jpg')"
                :alt="personaDisplayName"
                cover
              ></v-img>
            </v-avatar>
          </div>

          <v-card-text class="text-center pt-6">
            <v-chip-group class="justify-center">
              <v-chip
                v-if="!generated && persona.personal_characteristics.segmentation"
                color="primary"
                label
                class="font-weight-medium"
                variant="elevated"
              >
                {{ persona.personal_characteristics.segmentation }}
              </v-chip>
            </v-chip-group>
          </v-card-text>

          <!-- Motto when present on persona data (e.g. top_management workMotto) -->
          <v-card-text v-if="persona.workMotto" class="text-center pt-1 pb-6">
            <div class="motto-container">
              <v-icon icon="mdi-format-quote-open" size="small" class="mr-1" color="primary" />
              <span class="motto-text">{{ persona.workMotto }}</span>
              <v-icon icon="mdi-format-quote-close" size="small" class="ml-1" color="primary" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Personal Characteristics -->
      <v-col cols="12" md="9">
        <v-card class="characteristics-card rounded-xl elevation-3 h-100">
          <v-card-title class="d-flex align-center py-4 px-6 primary-bg">
            <v-icon icon="mdi-account-details" class="mr-2" color="white"></v-icon>
            <span class="text-h5 white--text font-weight-bold">{{ t('personainfo.personalProfile') }}</span>
          </v-card-title>

          <v-card-text class="pa-6">
            <v-row>
              <!-- Left column - Personal Characteristics -->
              <v-col cols="12" md="6">
                <h3 class="subtitle-heading mb-4">
                  <v-icon icon="mdi-card-account-details" class="mr-2" color="primary"></v-icon>
                  {{ t('personainfo.demographics') }}
                </h3>

                <v-list class="personal-details pa-0" v-if="(!generated && demographicEntries)">
                  <v-list-item v-for="(entry, idx) in demographicEntries" :key="'demo-'+idx" class="px-1">
                    <template v-slot:prepend>
                      <v-icon :icon="entry.icon" color="primary" class="mr-3"></v-icon>
                    </template>
                    <v-list-item-title>
                      <strong>{{ entry.label }}:</strong> {{ entry.value }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>

                <v-list class="personal-details pa-0" v-if="generated && generatedDemographics.length">
                  <v-list-item v-for="(entry, idx) in generatedDemographics" :key="'gen-demo-'+idx" class="px-1">
                    <template v-slot:prepend>
                      <v-icon :icon="entry.icon" color="primary" class="mr-3"></v-icon>
                    </template>
                    <v-list-item-title>
                      <strong>{{ entry.label }}:</strong> {{ entry.value }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>

              <!-- Right column - Personality and Interests -->
              <v-col cols="12" md="6">
                <h3 class="subtitle-heading mb-4">
                  <v-icon icon="mdi-account-star" class="mr-2" color="primary"></v-icon>
                  {{ t('personainfo.characterProfile') }}
                </h3>

                <div class="personality-section mb-4" v-if="!generated">
                  <div v-for="(item, index) in (persona.personality || [])" :key="'personality-'+index" class="my-2 personality-item">
                    <v-icon icon="mdi-circle-small" color="primary" size="small" class="mr-1"></v-icon>
                    {{ item }}
                  </div>
                </div>

                <div class="d-flex flex-wrap mb-4" v-if="generated && generatedPersonality.length">
                  <v-chip
                    v-for="(trait, idx) in generatedPersonality"
                    :key="'gen-trait-'+idx"
                    color="primary"
                    variant="tonal"
                    label
                    class="ma-1 font-weight-medium"
                    size="small"
                  >
                    {{ trait }}
                  </v-chip>
                </div>

                <h3 class="subtitle-heading mb-3 mt-6">
                  <v-icon icon="mdi-heart-multiple" class="mr-2" color="primary"></v-icon>
                  {{ t('personagrid.interests') }}
                </h3>

                <div class="d-flex flex-wrap mb-4" v-if="!generated">
                  <v-chip
                    v-for="(interest, index) in (persona.personal_interests || [])"
                    :key="'interest-'+index"
                    color="secondary"
                    variant="elevated"
                    label
                    class="ma-1 font-weight-medium"
                    size="small"
                  >
                    {{ interest }}
                  </v-chip>
                </div>

                <div class="d-flex flex-wrap mb-4" v-if="generated && generatedInterests.length">
                  <v-chip
                    v-for="(interest, idx) in generatedInterests"
                    :key="'gen-int-'+idx"
                    color="secondary"
                    variant="elevated"
                    label
                    class="ma-1 font-weight-medium"
                    size="small"
                  >
                    {{ interest }}
                  </v-chip>
                </div>

                <!-- Devices: show when the persona has any (data-driven, not domain-gated) -->
                <template v-if="displayDevices.length">
                  <h3 class="subtitle-heading mb-3 mt-6">
                    <v-icon icon="mdi-devices" class="mr-2" color="primary"></v-icon>
                    {{ t('personainfo.devices') }}
                  </h3>
                  <div class="d-flex flex-wrap">
                    <v-chip
                      v-for="(device, index) in displayDevices"
                      :key="'device-'+index"
                      color="info"
                      variant="outlined"
                      label
                      class="ma-1"
                      size="small"
                      prepend-icon="mdi-cellphone-link"
                    >
                      {{ device }}
                    </v-chip>
                  </div>
                </template>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Generated Persona Additional Panels -->
    <v-row class="mt-6" v-if="generated">
      <v-col cols="12">
        <v-expansion-panels variant="accordion" class="elevation-3 rounded-lg">
          <v-expansion-panel v-if="generatedServiceInfo.length">
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-icon icon="mdi-cellphone-wireless" class="mr-3" color="primary"></v-icon>
                <span class="text-subtitle-1 font-weight-bold">{{ t('personainfo.telecomProfile') }}</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-list class="pa-0">
                <v-list-item v-for="(entry, idx) in generatedServiceInfo" :key="'svc-'+idx" class="px-1">
                  <template v-slot:prepend>
                    <v-icon :icon="entry.icon" color="primary" class="mr-3"></v-icon>
                  </template>
                  <v-list-item-title>
                    <strong>{{ entry.label }}:</strong> {{ entry.value }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel v-if="generatedBigFiveItems.length">
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-icon icon="mdi-chart-bar" class="mr-3" color="primary"></v-icon>
                <span class="text-subtitle-1 font-weight-bold">{{ t('personainfo.bigFivePersonalityProfile') }}</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div v-for="(item, idx) in generatedBigFiveItems" :key="'gen-big5-'+idx" class="my-2 trait-description">
                <v-icon icon="mdi-circle-small" size="small" color="primary" class="mr-1"></v-icon>
                {{ item }}
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Additional Information Cards -->
    <v-row class="mt-6" v-if="!generated">
      <v-col cols="12">
        <v-expansion-panels variant="accordion" class="elevation-3 rounded-lg">
          <template v-for="(panel, pIndex) in dynamicPanels" :key="'panel-'+pIndex">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="d-flex align-center">
                  <v-icon :icon="panel.icon" class="mr-3" color="primary"></v-icon>
                  <span class="text-subtitle-1 font-weight-bold">{{ panel.title }}</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <template v-if="panel.type === 'big5'">
                  <v-row>
                    <v-col cols="12">
                      <div v-for="(value, key) in panel.data" :key="'big5-'+key" class="personality-trait-card mb-4">
                        <v-card variant="outlined" class="trait-card">
                          <v-card-item>
                            <v-card-title class="d-flex align-center">
                              <v-icon :icon="getTraitIcon(key)" color="primary" class="mr-2"></v-icon>
                              <span class="text-h6">{{ formatTraitName(key) }}</span>
                            </v-card-title>
                            <v-card-text>
                              <p class="trait-description">{{ capitalizeFirstLetter(value) }}</p>
                            </v-card-text>
                          </v-card-item>
                        </v-card>
                      </div>
                    </v-col>
                  </v-row>
                </template>
                <template v-else-if="panel.type === 'text'">
                  <p class="my-2 trait-description">{{ panel.data }}</p>
                </template>
                <template v-else>
                  <div v-for="(item, index) in panel.data" :key="'li-'+index" class="my-2">
                    <v-icon icon="mdi-circle-small" size="small" color="primary" class="mr-1"></v-icon>
                    {{ item }}
                  </div>
                </template>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </template>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  fetch_persona_domain_schema,
  fetch_personas_by_domain,
  fetch_generated_personas,
} from "@/api.js";
import {
  loadDomainPersonasFromApi,
  fetchPersonaById,
  normalizeAssetReference,
  resolvePersonaImage,
  resolveSavedPersonaFieldValue,
  staticAssetPath,
  toGeneratedPersonaView,
} from "@/utils/personaDomainLoader";
import { formatAttributeDisplayValue } from '@/utils/attributeValueCoercion';
import { canonicalizeSelectValue, expandSelectChoices } from "@/utils/selectChoices";
import { useI18n } from "vue-i18n";

// Keys that belong in the page title / avatar, not Demographics list.
const IDENTITY_ATTRIBUTE_KEYS = new Set(['first_name', 'last_name', 'name', 'surname']);

// Nested / special data keys — not promoted as flat demographic fields.
const RESERVED_DATA_KEYS = new Set([
  'personal_characteristics',
  'personalCharacteristics',
  'personal_interests',
  'personalInterests',
  'Personal Interests',
  'image',
  'description',
  'first_name',
  'last_name',
  'big_five_profile',
  'bigFiveProfile',
  'personality',
  'devices',
  // Seed-catalog bookkeeping (data.id), not a persona attribute.
  'id',
]);

const ATTRIBUTE_KEY_ALIASES = {
  occupation: ['occupation', 'job_title'],
  job_title: ['job_title', 'occupation'],
  first_name: ['first_name', 'name'],
  last_name: ['last_name', 'surname'],
  civil_status: ['civil_status', 'civilStatus', 'marital_status'],
  marital_status: ['marital_status', 'civil_status', 'civilStatus'],
  geographic_area_of_residence: ['geographic_area_of_residence', 'residence', 'area'],
  residence: ['residence', 'geographic_area_of_residence', 'area'],
  area: ['area', 'geographic_area_of_residence', 'residence'],
  qualification: ['qualification', 'education_level'],
  education_level: ['education_level', 'qualification'],
  income: ['income', 'annual_income'],
  annual_income: ['annual_income', 'income'],
  segments: ['segments', 'segmentation', 'segment'],
  segmentation: ['segmentation', 'segments', 'segment'],
  years_of_service: ['years_of_service', 'yearsOfService'],
  yearsOfService: ['yearsOfService', 'years_of_service'],
};

export default {
  props: ["id"],
  data() {
    return {
      personas: [],
      selectedDomain: 'telco',
      generated: false,
      personaData: null,
      schemaAttributes: [],
    };
  },
  setup() {
		const { t } = useI18n();

		return {
			t,
      staticAssetPath,
		};
	},
  computed: {
    persona() {
      if (this.generated) return this.personaData;
      const routeId = this.id;
      const fromList = this.personas.find((p) =>
        String(p.persona_id || p.id) === String(routeId)
      );
      if (fromList) return this.normalizePersona(fromList);
      // Fallback while loadDomain is in flight (PersonaGrid seeds sessionStorage).
      if (
        this.personaData
        && String(this.personaData.persona_id || this.personaData.id) === String(routeId)
      ) {
        return this.normalizePersona(this.personaData);
      }
      return null;
    },
    // Never concatenate data[0]/data[1] — Object.entries order puts description
    // first and personal_characteristics (an object) second → "[object Object]".
    personaDisplayName() {
      const p = this.persona;
      if (!p) return '';
      if (p.title || p.name) return p.title || p.name;

      const map = this.generatedDataMap;
      const pc = map.personal_characteristics || map.personalCharacteristics || {};
      const fromMap = `${map.first_name || map.name || pc.first_name || ''} ${map.last_name || map.surname || pc.last_name || ''}`.trim();
      if (fromMap) return fromMap;

      return this.t('personainfo.generatedPersona');
    },
    displayDevices() {
      if (this.generated) return this.generatedDevicesList;
      const list = this.persona?.devices;
      return Array.isArray(list) ? list : [];
    },
    generatedBigFiveItems() {
      const m = this.generatedDataMap;
      const value = m['Big five profile'] || m.big_five_profile || m.bigFiveProfile;
      if (Array.isArray(value)) return value;
      if (value && typeof value === 'object') {
        return Object.entries(value).map(([k, v]) => `${this.formatTraitName(k)}: ${v}`);
      }
      if (typeof value === 'string' && value) return [value];
      return [];
    },
    orderedSchemaAttributes() {
      return [...(this.schemaAttributes || [])]
        .filter((attr) => attr?.key && !IDENTITY_ATTRIBUTE_KEYS.has(attr.key))
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    },
    // Demographics follow persona_domain_schemas.display_order. Schema
    // attributes this persona has no value for are left out, not shown as "—".
    demographicEntries() {
      const pc = this.persona?.personal_characteristics || {};
      const persona = this.persona || {};
      const usedPcKeys = new Set();
      const entries = [];

      for (const attr of this.orderedSchemaAttributes) {
        const resolvedValue = resolveSavedPersonaFieldValue(persona, attr.key);
        if (typeof resolvedValue === 'object' && resolvedValue !== null) continue;
        // Mark every alias of this attribute as consumed, not just the
        // specific key the value resolved under — a legacy alias still
        // present on pc (e.g. income once the schema's annual_income has
        // been shown) must not be re-rendered as a separate "leftover"
        // attribute below.
        (ATTRIBUTE_KEY_ALIASES[attr.key] || [attr.key]).forEach((k) => usedPcKeys.add(k));
        if (resolvedValue === undefined || resolvedValue === null || resolvedValue === '') continue;
        entries.push({
          key: attr.key,
          label: attr.label || this.translateFieldLabel(attr.key),
          icon: this.getDemographicIcon(attr.key),
          value: formatAttributeDisplayValue(
            canonicalizeSelectValue(
              resolvedValue,
              attr.type === 'single_select' ? expandSelectChoices(attr.options?.choices) : [],
            ),
            attr,
          ),
        });
      }

      // Keep any leftover personal_characteristics fields not covered by schema
      // (legacy narrative fields), appended after schema-ordered ones.
      Object.keys(pc)
        .filter((k) => !usedPcKeys.has(k) && !IDENTITY_ATTRIBUTE_KEYS.has(k) && pc[k] !== undefined && pc[k] !== null && pc[k] !== '' && typeof pc[k] !== 'object')
        .forEach((k) => {
          entries.push({
            key: k,
            label: this.translateFieldLabel(k),
            icon: this.getDemographicIcon(k),
            value: pc[k],
          });
        });

      return entries;
    },
    generatedDataMap() {
      if (!this.generated || !this.persona?.data) return {};
      if (!Array.isArray(this.persona.data)) {
        return { ...this.persona.data };
      }
      return this.persona.data.reduce((acc, item) => {
        acc[item.key] = item.value;
        return acc;
      }, {});
    },
    generatedPersonality() {
      const m = this.generatedDataMap;
      const value = m.Personality || m.personality;
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value) return [value];
      return [];
    },
    generatedInterests() {
      const m = this.generatedDataMap;
      const value = m['Personal Interests'] || m.personal_interests || m.personalInterests;
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value) return [value];
      return [];
    },
    generatedDevicesList() {
      const fromPersona = this.persona?.devices;
      if (Array.isArray(fromPersona) && fromPersona.length) return fromPersona;
      const value = this.generatedDataMap.devices;
      return Array.isArray(value) ? value : [];
    },
    generatedDemographics() {
      const m = this.generatedDataMap;
      if (this.orderedSchemaAttributes.length) {
        return this.orderedSchemaAttributes
          .map((attr) => {
            const resolved = resolveSavedPersonaFieldValue(this.persona, attr.key)
              ?? this.resolveGeneratedValue(m, attr.key);
            if (typeof resolved === 'object' && resolved !== null && !Array.isArray(resolved)) {
              return null;
            }
            const hasValue = resolved !== undefined && resolved !== null && resolved !== ''
              && !(Array.isArray(resolved) && !resolved.length);
            if (!hasValue) return null;
            const formatted = formatAttributeDisplayValue(
              Array.isArray(resolved) ? resolved.join(', ') : resolved,
              attr,
            );
            return {
              key: attr.key,
              icon: this.getDemographicIcon(attr.key),
              label: attr.label || this.translateFieldLabel(attr.key),
              value: Array.isArray(formatted) ? formatted.join(', ') : formatted,
            };
          })
          .filter(Boolean);
      }

      // Fallback when schema is unavailable (legacy hardcoded order).
      const entries = [
        { key: 'age', icon: 'mdi-calendar-month' },
        { key: 'gender', icon: 'mdi-gender-male-female' },
        { key: 'marital_status', icon: 'mdi-heart' },
        { key: 'number_of_children', icon: 'mdi-account-child' },
        { key: 'education_level', icon: 'mdi-school' },
        { key: 'job_title', icon: 'mdi-briefcase-variant' },
        { key: 'annual_income', icon: 'mdi-currency-usd' },
        { key: 'area', icon: 'mdi-home-city' },
        { key: 'segments', icon: 'mdi-account-group' },
        { key: 'preferred_contact_method', icon: 'mdi-message-text' },
      ];
      return entries
        .map((e) => {
          const value = this.resolveGeneratedValue(m, e.key);
          if (value === undefined || value === null || value === '') return null;
          return {
            ...e,
            label: this.translateFieldLabel(e.key),
            value: Array.isArray(value) ? value.join(', ') : value,
          };
        })
        .filter(Boolean);
    },
    generatedServiceInfo() {
      // Data-driven service/operator fields — shown when present, not when domain === telco.
      const m = this.generatedDataMap;
      const entries = [
        { key: 'current_operator', icon: 'mdi-cellphone-wireless' },
        { key: 'plan_type', icon: 'mdi-card-account-details' },
        { key: 'active_services', icon: 'mdi-check-all' },
        { key: 'data_usage', icon: 'mdi-database' },
        { key: 'average_monthly_spend', icon: 'mdi-cash' },
        { key: 'churn_propensity', icon: 'mdi-alert-circle-outline' },
        { key: 'payment_terms', icon: 'mdi-calendar-clock' },
      ];
      return entries
        .filter(e => m[e.key] !== undefined && m[e.key] !== null && m[e.key] !== '')
        .map(e => ({
          ...e,
          label: this.translateFieldLabel(e.key),
          value: Array.isArray(m[e.key]) ? m[e.key].join(', ') : m[e.key]
        }));
    },
    dynamicPanels() {
      if (!this.persona) return [];
      const panels = [];
      const p = this.persona;
      const big5 = p.big_five_profile || p.bigFiveProfile;
      // Identity / UI bookkeeping — not narrative expansion panels.
      const excluded = new Set([
        'id','name','title','description','image','icons','created','tags','country',
        'personal_characteristics','personalCharacteristics','personal_interests','personalInterests',
        'devices','currentTechnologyAndTools','personality','big_five_profile','bigFiveProfile',
        'data','persona_id','domain','tags_raw','is_ai_generated','image_slug','created_at','updated_at',
        'workMotto','weekendRoutine',
      ]);
      // Object sections rendered as labeled lines when present on the persona.
      const objectSectionFields = ['customer_status', 'customer_engagement', 'online_behavior_and_energy_engagement'];

      Object.keys(p).forEach(key => {
        if (excluded.has(key)) return;
        const v = p[key];

        if (objectSectionFields.includes(key) && typeof v === 'object' && !Array.isArray(v) && v !== null) {
          const entries = Object.entries(v)
            .filter(([, val]) => val !== undefined && val !== null && val !== '')
            .map(([k, val]) => `${this.formatLabel(k)}: ${val}`);
          if (entries.length > 0) {
            panels.push({ title: this.sectionTitle(key), icon: this.sectionIcon(key), type: 'list', data: entries });
          }
          return;
        }

        if (Array.isArray(v) && v.length > 0) {
          const asText = v.filter((item) => typeof item === 'string');
          if (asText.length) {
            panels.push({ title: this.sectionTitle(key), icon: this.sectionIcon(key), type: 'list', data: asText });
          }
        }
      });
      if (p.weekendRoutine) {
        panels.push({ title: this.t('personainfo.weekendRoutine'), icon: 'mdi-calendar-week', type: 'text', data: p.weekendRoutine });
      }
      if (big5 && typeof big5 === 'object' && Object.keys(big5).length > 0) {
        panels.push({ title: this.t('personainfo.bigFivePersonalityProfile'), icon: 'mdi-chart-bar', type: 'big5', data: big5 });
      }
      return panels;
    }
  },
  async mounted() {
    try {
      this.generated = JSON.parse(sessionStorage.getItem("generated"));
    } catch {
      this.generated = false;
    }
    try {
      this.personaData = JSON.parse(sessionStorage.getItem("personaData"));
    } catch {
      this.personaData = null;
    }

    const domain = (this.$route.query.domain || 'telco').toLowerCase();
    this.selectedDomain = domain || 'telco';
    await Promise.all([
      this.loadDomain(this.selectedDomain),
      this.loadSchema(this.selectedDomain),
    ]);
  },
  watch: {
    '$route.query.domain': {
      immediate: false,
      async handler(newVal) {
        const d = (newVal || 'telco').toLowerCase();
        this.selectedDomain = d || 'telco';
        await Promise.all([
          this.loadDomain(this.selectedDomain),
          this.loadSchema(this.selectedDomain),
        ]);
      }
    }
  },
  methods: {
    goBack() {
      const query = { domain: this.selectedDomain };
      if (this.generated) {
        query.view = 'generated';
      }
      this.$router.push({ name: 'persona', query });
    },
    translateFieldLabel(key) {
      // These three stay English regardless of locale, consistent with the
      // other attribute titles on this page — personaFields.occupation/
      // .qualification/.income are intentionally translated elsewhere (the
      // Chat/Interview persona filters), so the shared i18n keys stay as-is.
      if (key === 'occupation' || key === 'qualification' || key === 'income') {
        return { occupation: 'Occupation', qualification: 'Qualification', income: 'Income' }[key];
      }

      const translationKey = `personaFields.${key}`;
      const translated = this.t(translationKey);

      return translated !== translationKey
        ? translated
        : this.formatLabel(key);
    },
    async loadSchema(domain) {
      try {
        const response = await fetch_persona_domain_schema(domain);
        this.schemaAttributes = response.data?.attributes || [];
      } catch (error) {
        console.error('Failed to load persona domain schema for demographics:', error);
        this.schemaAttributes = [];
      }
    },
    resolveDemographicValue(pc, persona, key) {
      const candidates = ATTRIBUTE_KEY_ALIASES[key] || [key];
      const data = persona?.data && typeof persona.data === 'object' && !Array.isArray(persona.data)
          ? persona.data
          : null;

      for (const candidate of candidates) {
        if (pc[candidate] !== undefined && pc[candidate] !== null && pc[candidate] !== '') {
          return { value: pc[candidate], sourceKey: candidate };
        }
        if (persona[candidate] !== undefined && persona[candidate] !== null && persona[candidate] !== '') {
          return { value: persona[candidate], sourceKey: candidate };
        }
        if (data && data[candidate] !== undefined && data[candidate] !== null && data[candidate] !== '') {
          return { value: data[candidate], sourceKey: candidate };
        }
      }
      return { value: null, sourceKey: key };
    },
    resolveGeneratedValue(map, key) {
      const candidates = ATTRIBUTE_KEY_ALIASES[key] || [key];
      const pc = map.personal_characteristics || map.personalCharacteristics || {};
      for (const candidate of candidates) {
        if (map[candidate] !== undefined && map[candidate] !== null && map[candidate] !== '') {
          return map[candidate];
        }
        if (pc[candidate] !== undefined && pc[candidate] !== null && pc[candidate] !== '') {
          return pc[candidate];
        }
      }
      return undefined;
    },
    async loadDomain(domain) {
      this.personas = await loadDomainPersonasFromApi(domain, this.t);
      // If the route id is not in the manual list (e.g. generated), resolve it directly.
      const routeId = this.id;
      const found = this.personas.find(
        (p) => String(p.persona_id || p.id) === String(routeId),
      );
      if (!found && routeId) {
        const byId = await fetchPersonaById(domain, routeId);
        if (byId) {
          if (byId.is_ai_generated) {
            this.applyGeneratedPersonaView(byId);
          }
          this.personas = [...this.personas, byId];
        }
      } else if (found?.is_ai_generated && !this.generated) {
        this.applyGeneratedPersonaView(found);
      } else if (!this.generated && this.personaData?.tags?.includes?.('ai_generated')) {
        this.generated = true;
      }
    },
    applyGeneratedPersonaView(persona) {
      const view = toGeneratedPersonaView(persona);
      if (!view) return;
      this.generated = true;
      this.personaData = view;
    },
    async fetchBackendPersonas(domain) {
      try {
        const [manualRes, generatedRes] = await Promise.all([
          fetch_personas_by_domain(domain),
          fetch_generated_personas(domain),
        ]);
        const docs = [...(manualRes.data || []), ...(generatedRes.data || [])];
        const byName = new Map(docs.map((doc) => [(doc.name || '').trim().toLowerCase(), doc]));
        return { byName, docs };
      } catch (error) {
        console.error('Failed to load backend personas for PersonaInfo overlay:', error);
        return { byName: new Map(), docs: [] };
      }
    },
    findBackendMatch(staticPersona, byName, docs) {
      const key = (staticPersona.title || staticPersona.name || '').trim().toLowerCase();
      const byNameMatch = byName.get(key);
      if (byNameMatch) return byNameMatch;

      const image = normalizeAssetReference(this.getPersonaImage(staticPersona));
      const candidates = docs.filter((doc) => doc.data && normalizeAssetReference(doc.data.image) === image);
      return candidates.length === 1 ? candidates[0] : undefined;
    },
    // Same reconciliation as PersonaGrid / PersonaManagement: backend values
    // (including custom schema keys like height_2) win over static JSON.
    overlayBackendData(staticPersona, backendDoc) {
      const data = backendDoc.data || {};
      const beCharKey = data.personal_characteristics
          ? 'personal_characteristics'
          : (data.personalCharacteristics ? 'personalCharacteristics' : null);
      const beChars = beCharKey ? data[beCharKey] : null;
      const staticChars = staticPersona.personal_characteristics || staticPersona.personalCharacteristics || {};

      const merged = { ...staticPersona };
      merged.personal_characteristics = { ...staticChars, ...(beChars || {}) };
      merged.personalCharacteristics = merged.personal_characteristics;

      if (merged.personal_characteristics.age === undefined && data.age !== undefined) {
        merged.personal_characteristics.age = data.age;
      }
      if (!merged.personal_characteristics.occupation && data.job_title) {
        merged.personal_characteristics.occupation = data.job_title;
      }

      // Promote flat custom fields (height_2, etc.) so Demographics can resolve them.
      this.promoteFlatDataFields(merged, data);

      const beInterests = data.personal_interests || data.personalInterests || data['Personal Interests'];
      merged.personal_interests = beInterests || staticPersona.personal_interests || staticPersona.personalInterests || [];

      merged.country = data.country || staticPersona.country || '';
      if (merged.country && !merged.personal_characteristics.country) {
        merged.personal_characteristics.country = merged.country;
      }

      merged.title = backendDoc.name || staticPersona.title || staticPersona.name;
      if (data.image) {
        merged.image = data.image;
      }
      if (data.description) {
        merged.description = data.description;
      }

      merged.persona_id = backendDoc.persona_id;
      merged.domain = backendDoc.domain || staticPersona.domain;
      // Keep a reference so resolveDemographicValue can still read raw data.*.
      merged.data = data;

      return merged;
    },
    promoteFlatDataFields(merged, data) {
      if (!data || typeof data !== 'object') return;
      const pc = merged.personal_characteristics || (merged.personal_characteristics = {});
      for (const [key, value] of Object.entries(data)) {
        if (RESERVED_DATA_KEYS.has(key)) continue;
        if (value === undefined || value === null || value === '') continue;
        if (typeof value === 'object') continue;

        // Don't promote a flat field as a new, distinct attribute when an
        // alias of it (ATTRIBUTE_KEY_ALIASES) already has a value on pc —
        // e.g. flat data.annual_income vs. an existing
        // personal_characteristics.income: same concept, not two separate
        // attributes. Backfill into whichever alias key already exists on
        // pc (even if currently empty) instead of adding a new key name;
        // fall back to the flat key itself when no alias is present at all.
        const aliasCandidates = ATTRIBUTE_KEY_ALIASES[key] || [key];
        const hasAliasValue = aliasCandidates.some((k) => pc[k] !== undefined && pc[k] !== null && pc[k] !== '');
        if (!hasAliasValue) {
          const targetKey = aliasCandidates.find((k) => k in pc) || key;
          pc[targetKey] = value;
        }

        if (merged[key] === undefined || merged[key] === null || merged[key] === '') {
          merged[key] = value;
        }
      }
    },
    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    formatLabel(key) {
      // Support snake_case and camelCase
      if (!key) return '';
      const spaced = key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .trim();
      return spaced.split(' ').map(w => this.capitalizeFirstLetter(w)).join(' ');
    },
    formatTraitName(key) {
      // Create a more readable format for the trait names
      const formatted = key.replace(/_/g, ' ');
      return formatted.split(' ').map(word => this.capitalizeFirstLetter(word)).join(' ');
    },
    // Interests list after removing items that appear in weekendRoutine (when present)
    filteredInterests(interests, routine) {
      if (!routine) return interests || [];
      const routineText = String(routine).toLowerCase();
      return (interests || []).filter(it => {
        const base = String(it).replace(/\(.*?\)/g, '').trim().toLowerCase();
        const token = base.length < 4 ? base : base;
        return token && !routineText.includes(token);
      });
    },
    sectionTitle(key) {
      const map = {
        online_behavior_and_use_of_services: this.t('personainfo.onlineBehavior'),
        onlineBehaviorAndProfessionalEngagement: this.t('personainfo.onlineBehavior'),
        connection_type_and_usage: this.t('personainfo.connectionUsage'),
        automotive_behavior_and_usage: this.t('personainfo.transportationBehaviour'),
        work_patterns_and_professional_usage: this.t('personainfo.workPatternsAndUsage'),
        workPatternsAndProfessionalUsage: this.t('personainfo.workPatternsAndUsage'),
        main_needs: this.t('personainfo.mainNeeds'),
        mainNeeds: this.t('personainfo.mainNeeds'),
        currentTechnologyAndTools: this.t('personainfo.technologyAndTools'),
        customer_status: this.t('personainfo.customerStatus'),
        customer_engagement: this.t('personainfo.customerEngagement'),
        energy_usage_context: this.t('personainfo.energyUsageContext'),
        online_behavior_and_energy_engagement: this.t('personainfo.onlineBehaviorAndEnergyEngagement'),
      };

      return map[key] || this.formatLabel(key);
    },
    sectionIcon(key) {
      const icons = {
        online_behavior_and_use_of_services: 'mdi-web',
        onlineBehaviorAndProfessionalEngagement: 'mdi-web',
        connection_type_and_usage: 'mdi-wifi',
        automotive_behavior_and_usage: 'mdi-speedometer',
        work_patterns_and_professional_usage: 'mdi-briefcase-clock-outline',
        workPatternsAndProfessionalUsage: 'mdi-briefcase-clock-outline',
        main_needs: 'mdi-lightbulb',
        mainNeeds: 'mdi-lightbulb',
        currentTechnologyAndTools: 'mdi-laptop',
        customer_status: 'mdi-account-check',
        customer_engagement: 'mdi-handshake-outline',
        energy_usage_context: 'mdi-flash',
        online_behavior_and_energy_engagement: 'mdi-web',
      };
      return icons[key] || 'mdi-folder-information';
    },
    getTraitIcon(trait) {
      // Return an appropriate icon for each personality trait
      const icons = {
        openness_to_experience: 'mdi-lightbulb-outline',
        conscientiousness: 'mdi-check-circle-outline',
        extroversion: 'mdi-account-group-outline',
        agreeableness: 'mdi-handshake',
        emotional_stability: 'mdi-emoticon-outline'
      };

      return icons[trait] || 'mdi-star';
    },
    getDemographicIcon(key) {
      const map = {
        age: 'mdi-calendar-month',
        gender: 'mdi-gender-male-female',
        occupation: 'mdi-briefcase-variant',
        job_title: 'mdi-briefcase-variant',
        civil_status: 'mdi-heart',
        civilStatus: 'mdi-heart',
        marital_status: 'mdi-heart',
        geographic_area_of_residence: 'mdi-home-city',
        residence: 'mdi-home-city',
        area: 'mdi-home-city',
        qualification: 'mdi-school',
        education_level: 'mdi-school',
        income: 'mdi-currency-usd',
        annual_income: 'mdi-currency-usd',
        segmentation: 'mdi-account-group',
        segments: 'mdi-account-group',
        country: 'mdi-earth',
        years_of_service: 'mdi-timer',
        yearsOfService: 'mdi-timer',
        number_of_children: 'mdi-account-child',
        preferred_contact_method: 'mdi-message-text',
        payment_terms: 'mdi-calendar-clock',
        current_operator: 'mdi-cellphone-wireless',
        plan_type: 'mdi-card-account-details',
        active_services: 'mdi-check-all',
        data_usage: 'mdi-database',
        average_monthly_spend: 'mdi-cash',
        churn_propensity: 'mdi-alert-circle-outline',
        height: 'mdi-human-male-height',
        height_2: 'mdi-human-male-height',
        height_3: 'mdi-human-male-height',
      };
      if (map[key]) return map[key];
      if (/^height(_\d+)?$/i.test(key)) return 'mdi-human-male-height';
      return 'mdi-card-account-details';
    },
    normalizePersona(p) {
      if (!p) return p;
      const n = { ...p };
      n.title = n.title || n.name;
      // Merge personal characteristics from snake_case and camelCase
      const rawData = (!Array.isArray(n.data) && n.data && typeof n.data === 'object')
        ? n.data
        : {};
      const pc = {
        ...(rawData.personal_characteristics || {}),
        ...(rawData.personalCharacteristics || {}),
        ...(n.personal_characteristics || {}),
        ...(n.personalCharacteristics || {}),
      };
      if (!pc.country && (n.country || rawData.country)) {
        pc.country = n.country || rawData.country;
      }

      // Promote flat scalar fields (annual_income, custom attrs, …) so
      // demographics resolve the same way as Edit Persona / Management.
      for (const [key, value] of Object.entries(rawData)) {
        if (RESERVED_DATA_KEYS.has(key)) continue;
        if (value === undefined || value === null || value === '') continue;
        if (typeof value === 'object') continue;

        const aliasCandidates = ATTRIBUTE_KEY_ALIASES[key] || [key];
        const hasAliasValue = aliasCandidates.some(
          (aliasKey) => pc[aliasKey] !== undefined && pc[aliasKey] !== null && pc[aliasKey] !== '',
        );
        if (!hasAliasValue) {
          const targetKey = aliasCandidates.find((aliasKey) => aliasKey in pc) || key;
          pc[targetKey] = value;
        }
        if (pc[key] === undefined || pc[key] === null || pc[key] === '') {
          pc[key] = value;
        }
        if (n[key] === undefined || n[key] === null || n[key] === '') {
          n[key] = value;
        }
      }

      n.personal_characteristics = pc;
      n.personalCharacteristics = pc;
      if (Object.keys(rawData).length) {
        n.data = rawData;
      }
      // Normalize common arrays
      // Remove from interests any items overlapping with weekendRoutine (Top Management only)
      const rawInterests = n.personal_interests || n.personalInterests
        || rawData.personal_interests || rawData.personalInterests || [];
      n.personal_interests = this.filteredInterests(rawInterests, n.weekendRoutine || rawData.weekendRoutine);
      // Devices from persona / nested data (empty → section hidden via displayDevices)
      n.devices = Array.isArray(n.devices) && n.devices.length
        ? n.devices
        : (Array.isArray(rawData.devices) ? rawData.devices : (n.currentTechnologyAndTools || []));
      if (!Array.isArray(n.personality) || !n.personality.length) {
        n.personality = Array.isArray(rawData.personality) ? rawData.personality : (n.personality || []);
      }
      // Promote section arrays/objects from nested data for expansion panels
      for (const [key, value] of Object.entries(rawData)) {
        if (RESERVED_DATA_KEYS.has(key)) continue;
        if (key === 'country' || key === 'image' || key === 'image_slug' || key === 'icons') continue;
        if (n[key] !== undefined && n[key] !== null && n[key] !== '') continue;
        if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
          n[key] = value;
        }
      }
      // Big Five
      n.big_five_profile = n.big_five_profile || n.bigFiveProfile
        || rawData.big_five_profile || rawData.bigFiveProfile || {};
      // Country fallback
      n.country = n.country || pc.country || pc.residence;
      // Ensure created exists
      n.created = n.created || new Date().getFullYear().toString();
      // Preserve energy / TM narrative fields when already present on data — do not invent empties by domain.
      n.workMotto = n.workMotto || rawData.workMotto;
      n.weekendRoutine = n.weekendRoutine || rawData.weekendRoutine;
      return n;
    },
    getPersonaImage(persona) {
      return resolvePersonaImage(persona);
    },
  },
};
</script>

<style scoped>
.persona-profile {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.header-section {
  position: relative;
}

.gradient-text {
  background: linear-gradient(to right, #3f51b5, #7c4dff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
}

.profile-card {
  background-color: white;
  position: relative;
}

.profile-header {
  height: 80px;
  background: linear-gradient(to right, #3f51b5, #7c4dff);
}

.profile-avatar {
  border: 4px solid white;
  background-color: white;
}

.primary-bg {
  background: linear-gradient(to right, #3f51b5, #7c4dff);
  color: white;
}

.subtitle-heading {
  font-size: 1.1rem;
  color: #3f51b5;
  font-weight: 600;
  display: flex;
  align-items: center;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.personality-item {
  font-weight: 400;
  font-style: italic;
  color: #424242;
}

.personality-trait {
  font-size: 0.95rem;
}

/* Make list items more compact */
.personal-details .v-list-item {
  min-height: 40px;
}

/* Add a subtle hover effect on cards */
.v-card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.v-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

/* Style expansion panels */
.v-expansion-panel {
  border-left: 3px solid #3f51b5;
  margin-bottom: 10px;
}

.v-expansion-panel-title {
  background-color: #f9fafd;
}

/* Motto styling for Top Management */
.motto-container {
  display: inline-flex;
  align-items: center;
  background: #f3f5ff;
  color: #3f51b5;
  border: 1px solid rgba(63,81,181,0.2);
  border-radius: 12px;
  padding: 8px 12px;
}
.motto-text {
  font-style: italic;
  font-weight: 600;
}
</style>
